import { useState, useEffect, useCallback } from "@wordpress/element";

/**
 * Switch
 *
 * Props:
 *  - checked (boolean)         -> controlled checked state
 *  - defaultChecked (boolean)  -> initial value for uncontrolled usage
 *  - onChange (fn)             -> (newValue) => void
 *  - disabled (boolean)
 *  - size ('sm'|'md'|'lg')     -> visual size
 *  - className (string)        -> extra wrapper class
 *  - ariaLabel (string)        -> accessibility label if no visible text
 */
export default function Switch({
    checked,
    defaultChecked = false,
    onChange,
    disabled = false,
    size = "md",
    className = "",
    ariaLabel = "Toggle",
}) {
    const [internal, setInternal] = useState(defaultChecked);
    const isControlled = typeof checked === "boolean";
    const value = isControlled ? checked : internal;

    useEffect(() => {
        // if switching from uncontrolled to controlled, keep internal in sync optionally
        if (isControlled) {
            setInternal(checked);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checked]);

    const toggle = useCallback(() => {
        if (disabled) return;
        const next = !value;
        if (!isControlled) setInternal(next);
        onChange?.(next);
    }, [disabled, isControlled, onChange, value]);

    const handleKey = useCallback(
        (e) => {
            if (disabled) return;
            if (e.key === " " || e.key === "Enter") {
                e.preventDefault();
                toggle();
            }
        },
        [disabled, toggle]
    );

    // size mapping
    const sizes = {
        sm: {
            track: "w-9 h-5",
            knob: "w-3.5 h-3.5",
            knobTranslate: "translate-x-[1.10rem]",
        },
        md: {
            track: "w-11 h-6",
            knob: "w-4 h-4",
            knobTranslate: "translate-x-6",
        },
        lg: {
            track: "w-14 h-7",
            knob: "w-5 h-5",
            knobTranslate: "translate-x-8",
        },
    };

    const s = sizes[size] || sizes.md;

    return (
        <button
            type="button"
            role="switch"
            aria-checked={value}
            aria-label={ariaLabel}
            onClick={toggle}
            onKeyDown={handleKey}
            disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            className={[
                "inline-flex items-center rounded-full transition-colors focus:outline-none",
                "focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-400",
                disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
                className,
            ].join(" ")}
        >
            <span
                // track
                className={[
                    "relative inline-flex items-center",
                    s.track,
                    "rounded-full transition-colors duration-200 ease-in-out",
                    value ? "bg-blue-600" : "bg-gray-200",
                ].join(" ")}
            >
                {/* knob */}
                <span
                    className={[
                        "inline-block bg-white rounded-full shadow transform transition-transform duration-200 ease-in-out",
                        s.knob,
                        // translate
                        value ? s.knobTranslate : "translate-x-1",
                        // subtle lift when active
                        value ? "translate-y-0" : "translate-y-0",
                    ].join(" ")}
                    style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.12)" }}
                />
            </span>
        </button>
    );
}
